import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { conceptsService } from '@/shared/api/services/concepts.service';
import { cacheKeys } from '@/shared/lib/cache-invalidation';

/**
 * Hook for prefetching adjacent concepts to improve navigation performance
 */
export function useConceptPrefetch() {
  const queryClient = useQueryClient();

  const prefetchConcept = useCallback(
    (projectId: string, conceptId: string) => {
      if (!projectId || !conceptId) return;

      // Check if data is already in cache and fresh
      const cachedData = queryClient.getQueryData(
        cacheKeys.concepts.detail(conceptId)
      );
      const queryState = queryClient.getQueryState(
        cacheKeys.concepts.detail(conceptId)
      );

      // If data exists and is not stale, skip prefetch
      if (cachedData && queryState && !queryState.isInvalidated) {
        // console.log(
        //   '[Prefetch] Skipping prefetch, concept already in cache:',
        //   conceptId
        // );
        return;
      }

      // console.log('[Prefetch] Prefetching concept:', conceptId);

      // Prefetch the concept with all includes
      queryClient.prefetchQuery({
        queryKey: cacheKeys.concepts.detail(conceptId),
        queryFn: async () => {
          // console.log('[Prefetch] Fetching data for concept:', conceptId);
          return await conceptsService.getConceptWithIncludes(
            projectId,
            conceptId
          );
        },
        staleTime: 5 * 60 * 1000, // 5 minutes - same as the main query
      });
    },
    [queryClient]
  );

  const prefetchAdjacentConcepts = useCallback(
    (
      projectId: string,
      prevConceptId: string | null,
      nextConceptId: string | null
    ) => {
      if (prevConceptId) {
        prefetchConcept(projectId, prevConceptId);
      }
      if (nextConceptId) {
        prefetchConcept(projectId, nextConceptId);
      }
    },
    [prefetchConcept]
  );

  return {
    prefetchConcept,
    prefetchAdjacentConcepts,
  };
}
